/**
 * SEO length hints for the metadata panel: the same thresholds the deploy analysis applies
 * server-side (ContentService.DeploymentAnalysis.cs), so an editor sees the warning while
 * typing instead of at publish time. Pure functions, unit-tested.
 */
export type SeoHintTone = "ok" | "warn" | "muted";

export type SeoHint = { count: number; tone: SeoHintTone; message: string };

/** Google shows ~50-60 characters of a title; the brand suffix is added by the site. */
export const SEO_TITLE_IDEAL_MIN = 30;
export const SEO_TITLE_IDEAL_MAX = 60;
export const SEO_TITLE_HARD_MAX = 65;
/** Google shows ~120-160 characters of a description. */
export const SEO_DESCRIPTION_IDEAL_MIN = 60;
export const SEO_DESCRIPTION_IDEAL_MAX = 160;
export const SEO_DESCRIPTION_HARD_MAX = 170;

const length = (value: string | null | undefined) => (value ?? "").trim().replace(/\s+/g, " ").length;

/** Hint for the SEO title (metadata.title falls back to the page title when empty). */
export function seoTitleHint(seoTitle: string | null | undefined, pageTitle: string | null | undefined): SeoHint {
	const effective = (seoTitle ?? "").trim() || (pageTitle ?? "").trim();
	const count = length(effective);
	if (count === 0) return { count, tone: "warn", message: "No title: the page would be indexed under the brand name only." };
	if (count > SEO_TITLE_HARD_MAX) return { count, tone: "warn", message: `Too long (${count}): Google cuts titles around ${SEO_TITLE_IDEAL_MAX} characters.` };
	if (count < SEO_TITLE_IDEAL_MIN) return { count, tone: "muted", message: `Short (${count}): ${SEO_TITLE_IDEAL_MIN}-${SEO_TITLE_IDEAL_MAX} characters describe the page best.` };
	if (count > SEO_TITLE_IDEAL_MAX) return { count, tone: "muted", message: `A bit long (${count}): aim for ${SEO_TITLE_IDEAL_MIN}-${SEO_TITLE_IDEAL_MAX} characters.` };
	return { count, tone: "ok", message: `Good length (${count}).` };
}

/** Hint for the meta description; an empty value is a warning (the site composes a fallback, but a written one ranks better). */
export function seoDescriptionHint(description: string | null | undefined): SeoHint {
	const count = length(description);
	if (count === 0) return { count, tone: "warn", message: "No description: the site will compose one from the page content; a written one performs better." };
	if (count > SEO_DESCRIPTION_HARD_MAX) return { count, tone: "warn", message: `Too long (${count}): Google cuts descriptions around ${SEO_DESCRIPTION_IDEAL_MAX} characters.` };
	if (count < SEO_DESCRIPTION_IDEAL_MIN) return { count, tone: "muted", message: `Short (${count}): ${SEO_DESCRIPTION_IDEAL_MIN}-${SEO_DESCRIPTION_IDEAL_MAX} characters give searchers a real summary.` };
	if (count > SEO_DESCRIPTION_IDEAL_MAX) return { count, tone: "muted", message: `A bit long (${count}): aim for ${SEO_DESCRIPTION_IDEAL_MIN}-${SEO_DESCRIPTION_IDEAL_MAX} characters.` };
	return { count, tone: "ok", message: `Good length (${count}).` };
}

/** Storage key of a share-image value (string or { src }). */
export function shareImageKey(value: unknown): string {
	if (typeof value === "string") return value.trim();
	if (value && typeof value === "object" && typeof (value as { src?: unknown }).src === "string") {
		return ((value as { src: string }).src ?? "").trim();
	}
	return "";
}
